import React, { useEffect, useRef, useState } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import axios from "axios";

// How this beautician appears to customers on their bookings: the photo
// (only with the beautician's agreement), rating and what customers praise.
const PROFILE = gql`
  query getBeauticianProfile($employeeId: ID!) {
    getBeauticianProfile(employeeId: $employeeId) {
      status
      message
      profile { employeeId firstName photo rating ratingCount ratingHidden servicesDone praise { label count } }
    }
  }
`;

const SET_PHOTO = gql`
  mutation setBeauticianCustomerPhoto($data: BeauticianPhotoInput!) {
    setBeauticianCustomerPhoto(data: $data) {
      status
      message
      profile { employeeId firstName photo rating ratingCount ratingHidden servicesDone praise { label count } }
    }
  }
`;

const UPLOAD_URL = "https://api.barbera.in/img/upload";
const absolute = (url) => (url && url.startsWith("/") ? `https://api.barbera.in${url}` : url);

export default function BeauticianCustomerProfile({ employeeId, fullName }) {
  const { data, loading, refetch } = useQuery(PROFILE, { variables: { employeeId }, fetchPolicy: "network-only", skip: !employeeId });
  const [save, saving] = useMutation(SET_PHOTO);
  const profile = data?.getBeauticianProfile?.profile;
  const [photo, setPhoto] = useState(null);
  const [consent, setConsent] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    setPhoto(profile?.photo || null);
    setConsent(!!profile?.photo);
  }, [profile?.photo]);

  const upload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!/^image\//.test(file.type)) return setMessage({ error: true, text: "Choose an image file." });
    if (file.size > 5 * 1024 * 1024) return setMessage({ error: true, text: "Keep the photo under 5 MB." });
    setUploading(true);
    setMessage(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data: res } = await axios.post(UPLOAD_URL, form);
      if (!res || res.success !== true || !res.url) throw new Error("Upload failed");
      setPhoto(absolute(res.url));
      setConsent(false); // a new photo needs a fresh agreement
    } catch (err) {
      setMessage({ error: true, text: `Upload failed: ${err.message}` });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (nextPhoto) => {
    setMessage(null);
    const { data: res } = await save({ variables: { data: { employeeId, photo: nextPhoto, consent: nextPhoto ? consent : false } } });
    const r = res?.setBeauticianCustomerPhoto;
    setMessage({ error: r?.status !== "SUCCESS", text: r?.message || "Something went wrong" });
    if (r?.status === "SUCCESS") refetch();
  };

  const changed = (photo || null) !== (profile?.photo || null) || (photo && consent !== !!profile?.photo);
  const first = profile?.firstName || (fullName || "").trim().split(/\s+/)[0] || "Beautician";
  const summary = [
    profile?.rating != null && !profile.ratingHidden ? `★ ${profile.rating.toFixed(1)} (${profile.ratingCount})` : null,
    profile?.servicesDone >= 10 ? `${profile.servicesDone} services` : null,
  ].filter(Boolean).join(" · ");

  return (
    <div className="w-full lg:w-5/6 rounded-xl border border-[#2a2a2a] bg-[#141414] p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h4 className="font-be-jost-pro font-semibold text-lg text-white">Shown to customers</h4>
          <p className="text-sm text-[#9a9a9a]">Their photo, rating and praise on the customer's booking.</p>
        </div>
        {loading && <span className="text-xs text-[#9a9a9a]">Loading…</span>}
      </div>

      <div className="mt-4 flex flex-col lg:flex-row gap-6">
        {/* Preview: what the customer's booking shows */}
        <div className="w-full lg:w-1/2 rounded-xl bg-white p-4">
          <p className="text-xs font-semibold text-gray-500 mb-3">Customer app preview</p>
          <div className="flex items-center gap-3">
            {photo ? (
              <img src={photo} alt={first} className="h-14 w-14 rounded-full object-cover" />
            ) : (
              <div className="h-14 w-14 rounded-full bg-gradient-to-br from-[#7c4dff] to-[#5b2bd6] text-white flex items-center justify-center text-xl font-bold">
                {first.charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <div className="font-semibold text-gray-900">{first}</div>
              <div className="text-sm text-gray-600">{summary || "Your beautician"}</div>
            </div>
          </div>
          {profile?.ratingHidden && (
            <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Rating ★ {profile.rating.toFixed(1)} ({profile.ratingCount}) is below 4.5, so customers don't see it.
            </p>
          )}
          {profile?.praise?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {profile.praise.map((p) => (
                <span key={p.label} className="rounded-full bg-green-50 px-3 py-1 text-xs font-medium text-green-700">
                  {p.label} · {p.count}
                </span>
              ))}
            </div>
          )}
          <p className="mt-3 text-[11px] text-gray-400">
            Stars show after 10 customer ratings, and only at 4.5 or above. Praise shows when picked 3+ times in the last year.
          </p>
        </div>

        {/* Photo + consent */}
        <div className="w-full lg:w-1/2 flex flex-col gap-3">
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => fileRef.current && fileRef.current.click()}
              disabled={uploading}
              className="rounded-lg bg-[#FBB033] px-4 py-2 text-sm font-semibold text-black disabled:opacity-50"
            >
              {uploading ? "Uploading…" : photo ? "Replace photo" : "Upload photo"}
            </button>
            {photo && (
              <button type="button" onClick={() => setPhoto(null)} className="rounded-lg border border-[#444] px-4 py-2 text-sm text-white">
                Remove
              </button>
            )}
          </div>
          <p className="text-xs text-[#9a9a9a]">A clear, friendly face photo in uniform works best. Square, at least 400×400.</p>
          {photo && (
            <label className="flex items-start gap-2 text-sm text-white cursor-pointer">
              <input type="checkbox" className="mt-1" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
              <span>{first} has agreed that customers can see this photo on their bookings.</span>
            </label>
          )}
          <button
            type="button"
            onClick={() => submit(photo)}
            disabled={!changed || saving.loading || uploading || (photo && !consent)}
            className="self-start rounded-lg border border-[#FBB033] px-4 py-2 text-sm font-semibold text-[#FBB033] disabled:opacity-40"
          >
            {saving.loading ? "Saving…" : photo ? "Save photo for customers" : "Save (no photo)"}
          </button>
          {message && <p className={`text-sm ${message.error ? "text-red-400" : "text-green-400"}`}>{message.text}</p>}
        </div>
      </div>
    </div>
  );
}
